import React from "react";

import { assertNever } from "@impero-com/common/utils/obj";
import { isEqual } from "lodash-es";

import { TimePicker, Time } from "components/timePicker";
import { MultiInputHelper } from "utils/multiInputHelper";

import { BaseFormDisplay } from "./formDisplay";

import { BaseFormField } from ".";

const helper: MultiInputHelper<Time, never> = new MultiInputHelper({
    isEqual: (a: Time, b: Time) => isEqual(a, b),
    isBlank: (_: Time) => false,
    display: (_: Time) => {
        throw new Error("MultiTimePicker has no display() function defined.");
    },
});

type MultiTimePickerProps =
    | ({ type: "EDIT" } & WrappedWriteProps)
    | ({ type: "READ_ONLY" } & WrappedReadProps);

type Wrapped<T> = T & { label: string };

interface ReadProps {
    name: string;
    values?: Time[];
}

type WrappedReadProps = Wrapped<ReadProps>;

interface WriteProps extends ReadProps {
    onChange: (v: Time | undefined) => void;
}

type WrappedWriteProps = Wrapped<WriteProps>;

class MultiTimePicker extends React.PureComponent<MultiTimePickerProps> {
    render() {
        const { props } = this;
        switch (props.type) {
            case "READ_ONLY": {
                const { label, name, values } = props;
                return (
                    <WrappedReadOnlyTimePicker
                        label={label}
                        name={name}
                        values={values}
                    />
                );
            }
            case "EDIT": {
                const { label, name, values, onChange } = props;
                return (
                    <WrappedEditTimePicker
                        label={label}
                        name={name}
                        values={values}
                        onChange={onChange}
                    />
                );
            }
            default:
                return assertNever(props);
        }
    }
}

type RawReadOnlyTimePickerProps = ReadProps;

class RawReadOnlyTimePicker extends React.PureComponent<RawReadOnlyTimePickerProps> {
    render() {
        const { value, placeholder } = helper.deriveValueAndPlaceholder(
            this.props.values
        );
        return (
            <TimePicker
                style={{ width: 140 }}
                format="HH:mm"
                value={value}
                disabled={true}
                placeholder={placeholder}
            />
        );
    }
}

type RawEditableTimePickerProps = WriteProps;

interface RawEditableTimePickerState {
    derivedValue: Time | undefined;
    trackedValue: Time | undefined;
    placeholder: string;
}

class RawEditableTimePicker extends React.Component<
    RawEditableTimePickerProps,
    RawEditableTimePickerState
> {
    state: RawEditableTimePickerState = {
        derivedValue: undefined,
        trackedValue: undefined,
        placeholder: "",
    };

    static getDerivedStateFromProps(
        props: RawEditableTimePickerProps,
        state: RawEditableTimePickerState
    ): RawEditableTimePickerState | null {
        const { value, placeholder } = helper.deriveValueAndPlaceholder(
            props.values
        );
        if (
            !isEqual(value, state.derivedValue) ||
            placeholder !== state.placeholder
        ) {
            return {
                derivedValue: value,
                trackedValue: value,
                placeholder,
            };
        }
        return null;
    }

    render() {
        const { trackedValue, placeholder } = this.state;

        return (
            <TimePicker
                style={{ width: 140 }}
                format="HH:mm"
                value={trackedValue}
                onChange={this.onChange}
                placeholder={placeholder}
                allowClear={false}
            />
        );
    }

    onChange = (value: Time | undefined): void => {
        this.setState({ trackedValue: value });
        this.props.onChange(value);
    };
}

class WrappedReadOnlyTimePicker extends React.PureComponent<WrappedReadProps> {
    render() {
        const { label, name, values } = this.props;
        return (
            <BaseFormDisplay label={label}>
                <RawReadOnlyTimePicker name={name} values={values} />
            </BaseFormDisplay>
        );
    }
}

class WrappedEditTimePicker extends React.PureComponent<WrappedWriteProps> {
    render() {
        const { label, name, values, onChange } = this.props;
        return (
            <BaseFormField
                getComponent={() => (
                    <RawEditableTimePicker
                        name={name}
                        values={values}
                        onChange={onChange}
                    />
                )}
                hasFeedback={false}
                label={label}
            />
        );
    }
}

type RawMultiTimePickerProps =
    | ({ type: "EDIT" } & WriteProps)
    | ({ type: "READ_ONLY" } & ReadProps);

class RawMultiTimePicker extends React.PureComponent<RawMultiTimePickerProps> {
    render() {
        const { props } = this;
        switch (props.type) {
            case "READ_ONLY": {
                const { name, values } = props;
                return <RawReadOnlyTimePicker name={name} values={values} />;
            }
            case "EDIT": {
                const { name, values, onChange } = props;
                return (
                    <RawEditableTimePicker
                        name={name}
                        values={values}
                        onChange={onChange}
                    />
                );
            }
            default:
                return assertNever(props);
        }
    }
}

export {
    MultiTimePicker,
    type MultiTimePickerProps,
    RawMultiTimePicker,
    type RawMultiTimePickerProps,
};
